import {
  observable,
  computed,
  subscribe,
  observableArray,
} from 'knockout-decorators'

import type {
  CartResponse,
  Offer,
  Person,
  Organization,
  OrderRequest,
  OrderResponse,
  CartRequest,
  UserDetails,
} from '@tixa/schema'
import {
  CartStatus,
  ShippingMode,
  PaymentMode,
  OrderStatus,
} from '@tixa/schema'

import * as Yup from 'yup'

import { Analytics, Authentication, Language } from '~/utils'
import { EventState } from '~/store/event'
import { PageState } from '~/store/page'

import { useCartTimeStorageTimer } from './store-cart-time'
import { useCartExpiryCheckTimer } from './expiry-checker'
import { useCartIdStorage } from './store-cart-id'
import {
  StoredOrderData,
  useOrderStore,
  markPaymentInitiatedForCartSync,
  clearPaymentInitiatedForCartSync,
} from './store-order'
import { useCartDataStore } from './store-cart-data'
import { isValidHungarianPostalCode } from '~/utils/postalCodes'

declare const EMBED: boolean

const SHIPPING_OFFER_ID = '-1001'
export class CartState {
  private Locale = Language.getInstance()

  // Default country constants - single source of truth
  public static readonly DEFAULT_COUNTRY_ID = 348
  public static readonly DEFAULT_COUNTRY = {
    id: 348,
    alpha2: 'hu',
    alpha3: 'hun',
    name: 'Magyarország',
  }

  public static get(): CartState {
    if (!CartState.instance) CartState.instance = new CartState()
    return CartState.instance
  }

  private static instance: CartState
  private pageState = PageState.get()
  private analytics = Analytics.getInstance()

  @observable public isInvoiceDifferent = false

  @observable public selectedCountryId = CartState.DEFAULT_COUNTRY_ID

  private cartTimeStorageTimer = useCartTimeStorageTimer()
  private cartExpiryTimer = useCartExpiryCheckTimer(
    () => this.isInQueue,
    async () => {
      if (this.analytics.checkoutExpired) {
        this.analytics.checkoutExpired()
      }
      await this.clear()
    },
    (remaining) => {
      this.remainingCartTime = remaining
    }
  )
  private cartIdStorage = useCartIdStorage()
  private orderStore = useOrderStore()
  private cartStore = useCartDataStore()

  @observable
  public isPrechecking = false

  @observable
  public isRequestOutdated = false

  @observable
  public isSubmittingOrder = false

  @observable
  public request?: CartRequest = undefined

  @observable
  public content?: CartResponse = undefined

  @observable
  public order?: OrderResponse = undefined

  @observable
  public hasUser = Authentication.getInstance().LoggedIn()

  // Cart form
  public formElement: HTMLFormElement | null = null

  public message(key: string): string {
    return this.Locale.translations[key]
  }

  private validationSchema = Yup.object().shape({
    email1: Yup.string()
      .email(() => this.message('cartFormEmailFormat'))
      .required(() => this.message('cartFormEmail')),
    name: Yup.string()
      .min(3, () => this.message('cartFormInputLength'))
      .required(() => this.message('cartFormName')),
    phone: Yup.string()
      .min(3, () => this.message('cartFormInputLength'))
      .required(() => this.message('cartFormPhone')),
    zip: Yup.string().when('$selectedCountryId', {
      is: CartState.DEFAULT_COUNTRY_ID,
      then: (schema) =>
        schema
          .min(4, () => this.message('cartFormZipLength'))
          .max(4, () => this.message('cartFormZipLength'))
          .test(
            'valid-hungarian-zip',
            () => this.message('cartFormZipInvalid'),
            (value) => {
              if (!value) return false
              return isValidHungarianPostalCode(value)
            }
          )
          .required(() => this.message('cartFormZip')),
      otherwise: (schema) =>
        schema
          .min(3, () => this.message('cartFormInputLength'))
          .required(() => this.message('cartFormZip')),
    }),
    city: Yup.string()
      .min(2, () => this.message('cartFormInputLength'))
      .required(() => this.message('cartFormCity')),
    address: Yup.string()
      .min(2, () => this.message('cartFormInputLength'))
      .required(() => this.message('cartFormAddress')),
    invoiceName: Yup.string().when(
      '$isInvoiceDifferent',
      (isInvoiceDifferent, schema) => {
        if (isInvoiceDifferent[0]) {
          return schema
            .min(3, () => this.message('cartFormInputLength'))
            .required(() => this.message('cartFormInvoiceName'))
        }

        return schema
      }
    ),
    invoiceVat: Yup.string().when(
      '$isInvoiceDifferent',
      (isInvoiceDifferent, schema) => {
        if (isInvoiceDifferent[0]) {
          return schema
            .min(3, () => this.message('cartFormInputLength'))
            .required(() => this.message('cartFormInvoiceVat'))
        }
        return schema
      }
    ),
    invoiceZip: Yup.string().when(
      ['$isInvoiceDifferent', '$selectedCountryId'],
      {
        is: (isInvoiceDifferent: boolean, selectedCountryId: number) =>
          isInvoiceDifferent &&
          selectedCountryId === CartState.DEFAULT_COUNTRY_ID,
        then: (schema) =>
          schema
            .min(4, () => this.message('cartFormZipLength'))
            .max(4, () => this.message('cartFormZipLength'))
            .test(
              'valid-hungarian-zip',
              () => this.message('cartFormZipInvalid'),
              (value) => {
                if (!value) return false
                return isValidHungarianPostalCode(value)
              }
            )
            .required(() => this.message('cartFormInvoiceZip')),
        otherwise: (schema) =>
          schema.when('$isInvoiceDifferent', {
            is: true,
            then: (schema) =>
              schema
                .min(3, () => this.message('cartFormInputLength'))
                .required(() => this.message('cartFormInvoiceZip')),
            otherwise: (schema) => schema,
          }),
      }
    ),
    invoiceCity: Yup.string().when(
      '$isInvoiceDifferent',
      (isInvoiceDifferent, schema) => {
        if (isInvoiceDifferent[0]) {
          return schema
            .min(2, () => this.message('cartFormInputLength'))
            .required(() => this.message('cartFormInvoiceCity'))
        }
        return schema
      }
    ),
    invoiceAddress: Yup.string().when(
      '$isInvoiceDifferent',
      (isInvoiceDifferent, schema) => {
        if (isInvoiceDifferent[0]) {
          return schema
            .min(2, () => this.message('cartFormInputLength'))
            .required(() => this.message('cartFormInvoiceAddress'))
        }
        return schema
      }
    ),
    termAcceptCheckbox: Yup.boolean().oneOf([true], () =>
      this.message('cartFormTermAcceptCheckbox')
    ),
  })

  private getValidationSchema() {
    const schema = this.validationSchema

    if (this.content?.fields) {
      const dynamicFieldsSchema = this.content.fields.reduce(
        (shape, field, index) => {
          let fieldSchema: Yup.AnySchema = Yup.string()

          if (field.type === 'text') {
            fieldSchema = Yup.string()
          } else if (field.type === 'date') {
            fieldSchema = Yup.date()
              .nullable()
              .transform((value, originalValue) =>
                originalValue === '' ? null : value
              )
          } else if (field.type === 'check') {
            fieldSchema = Yup.boolean().oneOf(
              [true],
              this.message('requiredCheckbox')
            )
          }

          if (field.optional === false) {
            fieldSchema = fieldSchema.required(
              this.message('requiredCartField')
            )
          }

          shape[`field${index}-${field.id}`] = fieldSchema

          return shape
        },
        {} as { [key: string]: Yup.AnySchema }
      )

      if (this.content?.extraTerms) {
        this.content.extraTerms.forEach((term, index) => {
          dynamicFieldsSchema[`extra-term-${index}`] = Yup.boolean().oneOf(
            [true],
            this.message('requiredCheckbox')
          )
        })
      }

      return schema.shape(dynamicFieldsSchema)
    }
  }

  @observable
  public chosenOptionalOffers: string[] = []

  @observable
  public shippingMode: ShippingMode = ShippingMode.eticket

  @observable
  public paymentMode: PaymentMode = PaymentMode.card

  @observable selectedShippingSku = ''

  @observable
  public person?: Person = undefined

  @observable
  public organization?: Organization = undefined

  @observable
  public formValidated = false

  @observable
  public formIsValid = false

  // Terms
  @observable
  public termsAccepted = false

  @observableArray
  public extraTermsAccepted: string[] = []

  // Expiration
  @observable
  public expirationLimit = -1

  @observable
  public elapsedTime = -1

  @computed
  public get offers(): Offer[] {
    if (this.content && this.content.cart) {
      return this.content.cart
    }
    return []
  }

  @computed
  public get shippingOffers(): Offer[] {
    return this.offers.filter(
      (offer) => offer['@id'].toString() === SHIPPING_OFFER_ID
    )
  }

  @computed
  public get isInQueue(): boolean {
    return (
      this.content?.status === CartStatus.wait ||
      this.content?.status === CartStatus.queue
    )
  }

  @computed
  public get queue() {
    if (!this.isInQueue) {
      return null
    }
    return {
      cartId: this.content?.id,
      finished: (cart: CartResponse) => {
        console.debug('Queue finished')
        this.update(cart)
      },
      error: (cart: CartResponse) => {
        console.debug('Queue error')
        this.update(cart)
      },
    }
  }

  @computed
  public get error() {
    return this.hasError(this.content, this.order, this.hasCartExpired)
  }

  public hasError(
    cart?: CartResponse,
    order?: OrderResponse,
    hasExpired?: boolean
  ): string | null {
    const cartErrorStates: CartStatus[] = [
      CartStatus.couponused,
      CartStatus.error,
      CartStatus.onecart,
      CartStatus.soldout,
    ]
    const orderErrorStates: OrderStatus[] = [
      'nemmehet',
      'kodlejart',
      'kuponlejart',
      'kuponhasznalt',
    ]

    if (hasExpired) {
      return 'timesup'
    }

    if (cart?.status && cartErrorStates.includes(cart.status)) {
      return cart.status
    }

    if (order?.status && orderErrorStates.includes(order.status)) {
      return order.status
    }

    return null
  }

  @observable remainingCartTime = 0

  @computed
  public get hasCartExpired(): boolean {
    return this.remainingCartTime < 0
  }

  @computed
  public get formData(): UserDetails {
    return {
      person: this.person,
      invoice: this.organization,
    }
  }

  get orderRequest(): OrderRequest | null {
    if (!this.content || !this.content.id || !this.person) {
      return null
    }

    const cart = [
      ...this.content.cart.filter((offer) => offer.eligibleQuantity.value > 0),
      ...this.content.cart
        .filter((offer) => offer.sku === this.selectedShippingSku)
        .map((offer) => {
          return {
            ...offer,
            eligibleQuantity: {
              ...offer.eligibleQuantity,
              value: 1,
            },
          }
        }),
      ...this.content.cart
        .filter((offer) => this.chosenOptionalOffers.includes(offer['@id']))
        .map((offer) => {
          return {
            ...offer,
            eligibleQuantity: {
              ...offer.eligibleQuantity,
              value: 1,
            },
          }
        }),
    ]

    const fields = this.content?.fields
      ?.map((field) =>
        field.type === 'check'
          ? { ...field, value: field.value ? '1' : '0' }
          : field
      )
      .filter((field) => field.type === 'check' || field.value !== undefined)

    return {
      id: this.content.id,
      cart,
      payment: this.paymentMode,
      person: this.person,
      invoiceAddress: this.organization?.address,
      invoiceName: this.organization?.name,
      invoiceVat: this.organization?.vatID,
      code: this.content.code,
      coupon: this.content.coupon,
      cartCoupon: this.content.cartCoupon,
      fields,
      message: this.content.message,
      password: this.content.password,
      requestUrl: window['embedOrigin'] || document.location.href,
    }
  }

  constructor() {
    subscribe<UserDetails>(() => this.formData, this.cartStore.store)
  }

  public async createRequest(request: CartRequest) {
    this.request = request
    await this.runRequest()
  }

  public async runRequest() {
    if (this.request) {
      this.isRequestOutdated = false
      let result: CartResponse | null = null
      try {
        result = await this.pageState.api.cart.requestCart(this.request)
      } catch (e) {
        console.debug(e)
      }
      if (result) {
        this.update(result)
      }
    }
  }

  public async deleteCart() {
    if (this.content?.id) {
      try {
        await this.pageState.api.waitingRoom.delete(this.content.id)
      } catch (e) {
        console.debug(e)
      }
    }
  }

  public async clear() {
    await this.cartIdStorage.remove()
    await this.cartTimeStorageTimer.remove()
    if (!this.order) {
      clearPaymentInitiatedForCartSync()
    }
    if (!this.order && this.analytics.cartCleared && this.content) {
      this.analytics.cartCleared(this.content.id)
      await this.deleteCart()
    }
    console.debug('Clearing cart')
    this.update(null)
    this.hasUser = Authentication.getInstance().LoggedIn()
    this.formValidated = false
    this.isRequestOutdated = true
    EventState.get().cartRequest = null
  }

  public formLoaded = (e) => {
    this.formElement = e
  }

  // This function takes care of updating content received from the server
  private async update(response: CartResponse | null) {
    // When response is false-ish, clear state
    if (!response) {
      await this.cartTimeStorageTimer.stop()
      await this.cartExpiryTimer.stop()
      this.content = undefined
      return
    }

    const sendAnalyticsEvent =
      !this.content && typeof response?.id !== 'undefined'

    // Merge response with existing reponse
    this.content = {
      ...(this.content || {}),
      ...response,
    }

    if (!this.content.payment || this.content.payment.length === 0) {
      this.paymentMode = PaymentMode.free
    }
    if (
      this.content.payment &&
      this.content.payment.length > 0 &&
      !this.content.payment.includes(this.paymentMode)
    ) {
      this.paymentMode = this.content.payment[0]
    }

    // If the response has a cart ID, store it
    // and start checking timeout
    if (
      this.content?.id &&
      this.content.status &&
      (this.content.status === CartStatus.cart ||
        this.content.status === CartStatus.wait)
    ) {
      await this.cartIdStorage.store(this.content?.id)
      await this.cartTimeStorageTimer.start()
      this.remainingCartTime = await this.cartExpiryTimer.start(
        this.content?.expiry
      )
      if (sendAnalyticsEvent && this.analytics.checkoutStarted) {
        this.analytics.checkoutStarted(
          response.id.toString(),
          response.cart || []
        )
      }
    } else {
      await this.cartIdStorage.remove()
      await this.cartTimeStorageTimer.stop()
      await this.cartExpiryTimer.stop()
    }
  }

  public async updateFromResponse(response: CartResponse | null) {
    await this.update(response)
  }

  // CART COUPON

  public async applyCoupon(coupon: string) {
    try {
      if (!this.content?.id) {
        return 'cartNotFound'
      }
      const response = await this.pageState.api.cart.addCartCoupon(
        this.content?.id,
        coupon
      )
      if ('error' in response) {
        return response.error
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fields, ...restResponse } = response

      this.update(restResponse)
      return true
    } catch (e) {
      if (e instanceof Error) {
        return e.message
      }
      return String(e)
    }
  }

  public async removeCoupon(coupon: string) {
    try {
      if (!this.content?.id) {
        return 'cartNotFound'
      }
      const response = await this.pageState.api.cart.removeCartCoupon(
        this.content?.id,
        coupon
      )
      if ('error' in response) {
        return response.error
      }
      this.update(response)
      return true
    } catch (e) {
      if (e instanceof Error) {
        return e.message
      }
      return String(e)
    }
  }

  // SUBMIT ORDER
  public async submit(data, e?: Event) {
    if (e) e.preventDefault()
    if (this.isSubmittingOrder) return

    this.isSubmittingOrder = true

    const formData: { [key: string]: string | boolean } = {}

    const inputs = data.srcElement.querySelectorAll('input')
    formData['termAcceptCheckbox'] = this.termsAccepted

    inputs.forEach((input) => {
      if (input.type === 'checkbox') {
        return
      }
      formData[input.id] = input.value
      if (this.content?.fields) {
        this.content.fields.forEach((field, index) => {
          if (field.optional === false && field.type === 'check') {
            formData[`field${index}-${field.id}`] = field.value
          }
        })
      }
      if (this.content?.extraTerms) {
        this.content?.extraTerms.forEach((term, index) => {
          formData[`extra-term-${index}`] = this.extraTermsAccepted.includes(
            `extra-term-${index}`
          )
        })
      }
    })

    const validationResult = await this.validateCart(formData)
    if (!validationResult) {
      this.isSubmittingOrder = false
      return
    }

    const backToQueue =
      typeof validationResult === 'object' &&
      validationResult.status === CartStatus.wait

    if (backToQueue) {
      this.update({
        status: validationResult.status,
        id: 0,
        cart: [],
      })
    } else {
      await this.submitOrder()
    }
    this.isSubmittingOrder = false
  }

  private async validateCart(formValues) {
    this.formValidated = true

    try {
      const validationSchema = this.getValidationSchema()
      if (!validationSchema || !this.content) {
        return
      }
      await validationSchema.validate(formValues, {
        abortEarly: false,
        context: {
          isInvoiceDifferent: this.isInvoiceDifferent,
          selectedCountryId: this.selectedCountryId,
        },
      })

      return await this.pageState.api.waitingRoom.checkQueue(this.content.id)
    } catch (err) {
      const validationError = err as Yup.ValidationError
      const firstError =
        validationError.inner[0].path === 'termAcceptCheckbox' &&
        validationError.inner.some((error: Yup.ValidationError) =>
          error.path?.includes('field')
        )
          ? validationError.inner[1]
          : validationError.inner[0]

      validationError.inner.forEach((error: Yup.ValidationError) => {
        const inputId = error.path

        if (!this.formElement) {
          return
        }

        const errorMessageElement = this.formElement.querySelector(
          `[data-for="${inputId}"]`
        ) as HTMLElement
        if (errorMessageElement) {
          errorMessageElement.textContent = error.message
          errorMessageElement.classList.add('visible')
          const inputElement = this.formElement.querySelector(`#${inputId}`)
          if (inputElement) {
            inputElement.classList.add('is-invalid')
            const focusElement = this.formElement.querySelector(
              `[data-for="${firstError.path}"]`
            ) as HTMLElement
            focusElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
      })

      if (this.analytics.formInvalid) {
        this.analytics.formInvalid()
      }
      return
    }
  }

  private async submitOrder() {
    try {
      if (!this.orderRequest) {
        throw new Error('Order request is missing.')
      }
      this.order = await this.pageState.api.order.submitOrder(this.orderRequest)

      const eventState = EventState.get()

      if (
        this.order?.status === OrderStatus.success &&
        this.content &&
        this.formData.person
      ) {
        const orderData: StoredOrderData = {
          orderId: this.order?.id || '',
          cartId: this.content.id.toString(),
          event: eventState.event?.name || '',
          name: this.formData.person.name,
          city: this.formData.person.address.addressLocality,
          zip: this.formData.person.address.postalCode,
          email: this.formData.person.email,
          phone: this.formData.person.telephone,
          offers: this.content.cart,
        }

        markPaymentInitiatedForCartSync(this.content.id)
        window.dispatchEvent(new CustomEvent('payment-redirect-starting'))

        await this.orderStore.storeOrderData(orderData)

        if (this.analytics.paymentInfoAdded && this.analytics.paymentStarted) {
          this.analytics.paymentInfoAdded(
            this.content.cart,
            orderData,
            this.paymentMode
          )
          this.analytics.paymentStarted()
          this.injectPaymentForm()
        }
      }
    } catch (error) {
      /* TODO: HANDLE ERRORS  */
    }
  }

  private injectPaymentForm() {
    if (!this.order?.paymentForm) {
      return
    }

    if (EMBED) {
      window.parent.postMessage(
        { action: 'submitPaymentForm', form: this.order?.paymentForm },
        '*'
      )
      return
    }

    // Create container for payment form
    const formContainer = document.createElement('div')
    formContainer.setAttribute('hidden', 'true')
    formContainer.innerHTML = this.order.paymentForm
    document.body.appendChild(formContainer)

    // Get created form and submit it
    const formElements = formContainer.getElementsByTagName('form')
    if (formElements.length > 0) {
      const formElement = formElements[0]

      // We're leaving the webiste here
      formElement.submit()
    }
  }
}
